import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import utils from 'helpers/utils';
import assistant from 'helpers/assistant';
import constants from 'helpers/constants';
import logger from 'helpers/logger';
import {useEffectEvent, useTourDefinition, useTourState} from 'helpers/hooks/utils';
import {useLinkNavigate} from 'helpers/hooks/links';
import {useLocation, useSearchParams} from 'react-router-dom';
import tourUtils from 'helpers/tour';
import walk from 'helpers/walk';
import {
  useAuthClientId,
  useAuthorize,
  useAuthUser
} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import {useClientUserPatch} from 'services/client/user/user.utils';
import {useEntityApplyActionsBulk, useEntitySearchProfile} from 'services/entity/entity.hooks';
import {processEntityTagGroupEvidence} from 'services/entity/entity.utils';
import TourDialog from 'components/organisms/Dialogs/TourDialog/TourDialog';
import Blocker from 'components/atoms/Layout/Blocker/Blocker';
import Narration from 'components/molecules/Narrations/Narration/Narration';

export const TourContext = React.createContext(null)

export function useTour () {
  return React.useContext(TourContext);
}

const TourProvider = (props) => {
  const {
    children
  } = props;

  const runRef = useRef({});
  const controlRef = useRef({});
  const stoppedRef = useRef([]);
  const pathRef = useRef(null);
  const dataRef = useRef({});
  const reportsRef = useRef({});

  const [internalState, setInternalState] = useState({
    error: null,
    reason: null,
    asking: null,
    handover: null,
    spotlight: null,
    said: null,
    run: 0
  });

  const user = useAuthUser();
  const clientId = useAuthClientId();
  const [searchParams] = useSearchParams();

  const navigate = useLinkNavigate();
  const location = useLocation();
  const authorize = useAuthorize();

  const patchUser = useClientUserPatch();
  const applyActions = useEntityApplyActionsBulk();
  const entitySearchProfile = useEntitySearchProfile();

  const tourState = useTourState(null, {
    type: constants.appState.type.local,
    name: 'app'
  });

  const tour = utils.isDefined(tourState.tourName) ? constants.data.lookup('tourTypes', tourState.tourName) : null;
  const allowed = utils.validForTour(location);
  const step = utils.isDefined(tour) ? tourState.tourStep : null;
  const welcome = allowed && utils.isDefined(tour) && !utils.isDefined(step);
  const finished = step === constants.tour.steps.finished;
  const running = allowed && utils.isDefined(step) && !finished && !internalState.error;

  const definition = useTourDefinition(tour?.value);

  const descriptor = (utils.isDefined(step) && !finished) ? definition.steps?.find((s) => {
    return s.name === step;
  }) : null;

  const dialog = welcome ? definition.dialogs?.welcome : (
    (allowed && finished) ? definition.dialogs?.finish : null
  );

  const scene = definition.scenes?.find((s) => {
    return s.name === descriptor?.scene;
  }) ?? null;
  const sceneIdx = definition.scenes?.indexOf(scene) ?? -1;
  const progress = finished ? 100 : Math.round((Math.max(0, sceneIdx) / Math.max(1, definition.scenes?.length ?? 1)) * 100);

  const tourValues = useCallback(() => {
    return {...dataRef.current, reports: reportsRef.current};
  }, []);

  const narration = internalState.asking ? definition.texts.stop : (
    internalState.error ? (internalState.reason ?? definition.texts.failed) : (internalState.said ?? tourUtils.narration(descriptor?.narration, descriptor?.narrationValues, tourValues()))
  );

  const report = useCallback((key, value) => {
    reportsRef.current = {...reportsRef.current, [key]: value};
  }, []);

  const tasks = useMemo(() => {
    return {
      nameCollection: (action, values) => {
        const table = values.reports[action.source];
        const conversation = utils.toArray(table?.assistantState?.assistantConversation);
        const profile = assistant.lastProfile(conversation);

        if (profile) {
          return Promise.resolve(profile.name);
        } else {
          return entitySearchProfile.mutation.mutateAsync({query: table?.listState?.query, conversation: conversation})
            .then(({response}) => {
              return response?.data?.data?.name;
            });
        }
      },
      calibration: (action, values) => {
        const conversation = values.reports[action.source]?.assistantState?.assistantConversation;

        return Promise.resolve({
          calibrate: assistant.questionOptionIndex(conversation, constants.assistant.conversation.tilts.calibrate),
          decider: assistant.questionOptionIndex(conversation, constants.assistant.conversation.tilts.misfits)
        });
      },
      tagCollection: (action, values) => {
        const collection = values.reports[action.collection]?.data;
        const entities = utils.toArray(values.reports[action.entities]?.list?.data);

        return Promise.all(utils.toArray(collection?.tagGroups)
          .filter((tg) => {
            return utils.isDefined(tg.autoTagType) && tg.autoTagType !== constants.collection.autoTagTypes.noAutoAssign;
          })
          .map((tg) => {
            const entityIds = entities
              .filter((entity) => {
                const value = utils.toArray(entity.tagGroups).find((etg) => {
                  return +etg.groupId === +tg.groupId;
                })?.tags;

                return processEntityTagGroupEvidence(entity, {tagGroup: tg}, value).length === 0;
              })
              .map((entity) => {
                return entity.entityId;
              });

            if (entityIds.length > 0) {
              return applyActions.mutation.mutateAsync({
                entityIds: entityIds,
                actions: [{
                  action: constants.entity.bulkActions.applyToExisting,
                  collection: {
                    name: collection.name,
                    collectionId: collection.collectionId
                  }
                }, {
                  action: constants.entity.bulkActions.autoTagGroup,
                  tagGroup: {groupId: tg.groupId}
                }]
              });
            } else {
              return Promise.resolve();
            }
          }));
      }
    };
  }, [applyActions.mutation, entitySearchProfile.mutation]);

  const setTourDataEvent = useEffectEvent(tourState.setTourData);
  const createContext = useCallback(() => {
    const token = runRef.current.token;
    let failed = null;
    controlRef.current.marks = controlRef.current.marks ?? {};

    return {
      values: tourValues,
      marks: controlRef.current.marks,
      timing: definition.timing,
      navigate: navigate,
      setData: (changes) => {
        dataRef.current = {...dataRef.current, ...changes};
        setTourDataEvent?.(dataRef.current);
      },
      cancelled: () => token !== runRef.current.token,
      paused: () => controlRef.current.paused,
      fail: (error) => {
        failed = failed ?? error;
      },
      failed: () => failed,
      acting: (on) => {
        controlRef.current.acting = on;
      },
      run: (action) => {
        return tasks[action.task](action, tourValues());
      },
      narrate: (said) => {
        setInternalState(utils.updater({said: said}, true));
      },
      handover: (element) => {
        controlRef.current.handover = element;
        setInternalState((current) => {
          return current.handover !== element ? {...current, handover: element} : current;
        });
      },
      spotlight: (element) => {
        setInternalState((current) => {
          return current.spotlight !== element ? {...current, spotlight: element} : current;
        });
      }
    };
  }, [navigate, setTourDataEvent, definition.timing, tourValues, tasks]);

  const cancelRun = useCallback(() => {
    runRef.current.token = (runRef.current.token ?? 0) + 1;
    runRef.current.step = null;
    controlRef.current.paused = false;
    controlRef.current.acting = false;
    controlRef.current.handover = null;
    setInternalState((current) => {
      return (current.handover || current.spotlight) ? {...current, handover: null, spotlight: null} : current;
    });
  }, []);

  const resetRun = useCallback(() => {
    cancelRun();
    setInternalState(utils.updater({error: null, reason: null, said: null, asking: null}, true));
  }, [cancelRun]);

  const dropTourParam = useCallback(() => {
    if (searchParams.has('tour')) {
      navigate({
        to: utils.removePathKeys(location.pathname + location.search, 'tour'),
        keepSearchParams: false,
        replace: true
      });
    }
  }, [searchParams, navigate, location.pathname, location.search]);

  const setTourNameEvent = useEffectEvent(tourState.setTourName);
  const clearEvent = useEffectEvent(tourState.clear);
  const startTour = useCallback((type) => {
    dropTourParam();
    resetRun();
    clearEvent?.();
    setTourNameEvent?.(type);
  }, [dropTourParam, resetRun, setTourNameEvent, clearEvent]);

  const stopTour = useCallback((seen = true) => {
    dropTourParam();
    resetRun();
    stoppedRef.current = stoppedRef.current.concat([tourState.tourName]);
    clearEvent?.();

    if (seen && user && utils.isDefined(tourState.tourName)) {
      patchUser(clientId, user, {[`hasSeen${utils.upperFirst(tourState.tourName)}`]: true});
    }
  }, [tourState.tourName, user, clientId, patchUser, dropTourParam, resetRun, clearEvent]);

  const askStop = useCallback((reason) => {
    controlRef.current.paused = true;
    setInternalState(utils.updater({asking: reason}, true));
  }, []);

  const setTourStepEvent = useEffectEvent(tourState.setTourStep);
  const restartStep = useCallback((fromResume) => {
    cancelRun();
    setTourStepEvent?.(fromResume ? (descriptor?.resume ?? step) : step);

    setInternalState((current) => {
      return utils.updater({error: null, reason: null, asking: null, run: current.run + 1}, true)(current);
    });
  }, [cancelRun, descriptor?.resume, step, setTourStepEvent]);

  const continueTour = useCallback(() => {
    if (internalState.asking === 'navigate') {
      restartStep(true);
    } else {
      controlRef.current.paused = false;
      setInternalState(utils.updater({asking: null}, true));
    }
  }, [internalState.asking, restartStep]);

  const beginTour = useCallback((values) => {
    resetRun();
    runRef.current.resumed = true;
    controlRef.current.marks = {};
    dataRef.current = values;

    setTourDataEvent?.(values);
    setTourStepEvent?.(tourUtils.firstStep(definition, values) ?? constants.tour.steps.finished);
  }, [definition, resetRun, setTourStepEvent, setTourDataEvent]);

  const narrationActions = useMemo(() => {
    const stopAction = {
      label: definition.texts.stopTour,
      onClick: () => stopTour()
    };

    if (internalState.asking) {
      return [
        stopAction,
        {
          label: definition.texts.continueTour,
          color: 'primary',
          onClick: () => continueTour()
        }
      ];
    } else if (internalState.error) {
      return [
        {
          label: definition.texts.skipTour,
          onClick: () => stopTour()
        },
        {
          label: definition.texts.tryAgain,
          color: 'primary',
          onClick: () => restartStep(false)
        }
      ];
    } else {
      return [stopAction];
    }
  }, [internalState.asking, internalState.error, definition.texts, stopTour, continueTour, restartStep]);

  const context = useMemo(() => {
    return {
      tourActive: tourState.tourName ?? null,
      tourStep: step,
      tourData: tourState.tourData,
      startTour: startTour,
      stopTour: stopTour,
      report: report
    };
  }, [tourState.tourName, step, tourState.tourData, startTour, stopTour, report]);

  useLayoutEffect(() => {
    const run = runRef.current;

    return () => {
      cancelRun();
      run.resumed = false;
    };
  }, [cancelRun]);

  useLayoutEffect(() => {
    dataRef.current = tourState.tourData ?? {};
  }, [tourState.tourData]);

  useEffect(() => {
    if (!utils.isDefined(tourState.tourName) && user && utils.validForTour(location)) {
      const unseen = constants.data.tourTypes.find((t) => {
        return !stoppedRef.current.includes(t.value) && (
          t.value === searchParams.get('tour') ||
          (user[`hasSeen${utils.upperFirst(t.value)}`] === false && authorize({attribute: `tour.${t.value}`}))
        );
      });

      if (unseen) {
        let active = true;

        walk.waitForPage({poll: definition.timing.poll, max: definition.timing.page}, () => !active)
          .then((settled) => {
            if (settled) {
              startTour(unseen.value);
            }
          });

        return () => {
          active = false;
        };
      }
    }
  }, [tourState.tourName, user, location, searchParams, authorize, startTour, definition.timing]);

  useEffect(() => {
    if (running && runRef.current.step !== step) {
      if (!descriptor) {
        runRef.current.token = (runRef.current.token ?? 0) + 1;
        clearEvent?.();
      } else if (!runRef.current.resumed && descriptor.resume && descriptor.resume !== step) {
        runRef.current.resumed = true;
        setTourStepEvent?.(descriptor.resume);
      } else if (!tourUtils.stepApplies(descriptor, tourValues(), controlRef.current.marks)) {
        runRef.current.resumed = true;
        setTourStepEvent?.(tourUtils.nextStep(definition, step, tourValues(), controlRef.current.marks) ?? constants.tour.steps.finished);
      } else {
        runRef.current.resumed = true;
        runRef.current.step = step;
        setInternalState(utils.updater({said: null}, true));

        const context = createContext();
        logger.trace(`Tour ${tour.value} step ${step}`);
        tourUtils.runStep(descriptor, context)
          .then(() => {
            if (!context.cancelled()) {
              setTourStepEvent?.(tourUtils.nextStep(definition, step, tourValues(), context.marks) ?? constants.tour.steps.finished);
            }
          })
          .catch((error) => {
            if (!context.cancelled()) {
              logger.trace(`Tour ${tour.value} stopped at ${step}`, error);
              setInternalState(utils.updater({error: error?.message ?? 'The tour stopped', reason: error?.reason ?? descriptor.reason ?? null}, true));
            }
          });
      }
    }
  }, [running, step, descriptor, tour, definition, createContext, tourValues, setTourStepEvent, clearEvent, internalState.run]);

  useEffect(() => {
    if (!allowed) {
      cancelRun();
      runRef.current.resumed = false;
    }
  }, [allowed, cancelRun]);

  useEffect(() => {
    if (pathRef.current !== location.pathname) {
      const fromTourRoute = utils.validForTour({pathname: pathRef.current});
      pathRef.current = location.pathname;

      if (running && fromTourRoute && !controlRef.current.acting && !internalState.asking) {
        askStop('navigate');
      }
    }
  }, [location.pathname, running, internalState.asking, askStop]);

  useEffect(() => {
    if (running) {
      const block = (e) => {
        if (e.isTrusted && !controlRef.current.handover?.contains(e.target)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      const stopKeyDown = utils.observeEvent(window, 'keydown', block, {capture: true});
      const stopKeyUp = utils.observeEvent(window, 'keyup', block, {capture: true});

      return () => {
        stopKeyDown();
        stopKeyUp();
      };
    }
  }, [running]);

  const handleDialogClose = (e, reason) => {
    stopTour(finished || reason === 'cancelButtonClick');
  };

  const handleBlockerClick = () => {
    if (!internalState.asking) {
      askStop('click');
    }
  };

  return <React.Fragment>
    <TourContext.Provider value={context}>
      {children}
    </TourContext.Provider>
    {dialog ?
      <TourDialog open={true}
                  title={dialog.title}
                  description={dialog.description}
                  fields={dialog.fields}
                  onStart={beginTour}
                  onClose={handleDialogClose}/> : null}
    {running ?
      <Blocker hole={internalState.handover ?? internalState.spotlight}
               interactive={Boolean(internalState.handover)}
               onClick={handleBlockerClick}/> : null}
    {(allowed && utils.isDefined(tour) && !welcome && !finished) ?
      <Narration title={scene?.title ?? tour.label}
                 narration={narration}
                 aside={(running && !internalState.asking && !internalState.handover) ? scene?.aside : null}
                 error={Boolean(internalState.error)}
                 progress={progress}
                 actions={narrationActions}
                 onClose={stopTour}/> : null}
  </React.Fragment>
};

TourProvider.propTypes = {
  children: PropTypes.any
}

export default TourProvider;
